import { GateMovementMessageType as MessageType, MetricUnit } from '@/__generated__/graphql';
import { Formatters } from '@/hooks/useFormatters';
import { type AuTone } from '@/theme/au-compat';
import { describeGateCloseCode } from '@features/gateCommon/utils/gateCloseCode';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { readPayloadPosition } from '@features/gateCommon/utils/payloadPosition';
import { I18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import {
  ArrowDownward,
  ArrowUpward,
  BackHand,
  Battery4Bar,
  CheckCircleOutline,
  Engineering,
  ErrorOutline,
  HelpOutline,
  PanTool,
  SvgIconComponent,
  VerifiedUser,
  Wifi,
} from '@mui/icons-material';
import { FC, PropsWithChildren, ReactNode } from 'react';

export interface NotificationData {
  id: number;
  type: MessageType;
  timestamp: number;
  payload?: Record<string, string | number | null> | null;
  /**
   * Código real del evento del firmware (AMARU-862/US-005). `type` es su mapeo, y nueve códigos de
   * cierre distintos comparten el tipo `FINALIZED_WITH_ERROR`.
   */
  rawCode?: string | null;
}

/**
 * Los cuatro tonos del feed. Es **uno más** que los de un estado de movimiento (`MovementTone`):
 * un mensaje puede avisar sin que la operación haya salido mal —límite alcanzado, modo manual,
 * detención remota— y eso es `warning`, que no es un estado en el que un movimiento pueda estar.
 */
export type NotificationTone = Extract<AuTone, 'info' | 'success' | 'warning' | 'error'>;

type Attributes = {
  Icon: SvgIconComponent;
  /**
   * El tono, no un color: la tarjeta lo resuelve contra `AU_TINT` con `auToneSurfaceSx`, que es la
   * misma receta que llevan la banda de estado y el chip de la tabla de movimientos.
   */
  tone: NotificationTone;
};

const DefaultAccent: FC<PropsWithChildren> = ({ children }) => children;

type TextFormatters = {
  titleFormatter: (
    payload: NotificationData['payload'],
    Accent?: FC<PropsWithChildren>,
  ) => ReactNode;
  contentFormatter?: (payload: NotificationData['payload']) => ReactNode;
};

/**
 * El mensaje sin color va en `info`, el acromático de la capa, y no en el `#222B36` que llevaba:
 * aquel era una superficie —el fondo de una card—, no un tono, así que el mensaje neutro no
 * pertenecía al mismo eje que sus tres hermanos y los cuatro no se leían como una familia.
 */
export const getAttributes = (type: MessageType): Attributes => {
  const DEFAULT_ATTRIBUTES: Attributes = { Icon: HelpOutline, tone: 'info' };

  const iconMap: Record<MessageType, Attributes> = {
    [MessageType.Initializing]: { Icon: Wifi, tone: 'info' },
    [MessageType.StopOperation]: { Icon: PanTool, tone: 'warning' },
    [MessageType.Complete]: { Icon: CheckCircleOutline, tone: 'success' },
    [MessageType.Battery]: { Icon: Battery4Bar, tone: 'info' },
    [MessageType.Movement]: { Icon: ArrowUpward, tone: 'info' },
    [MessageType.ValidateMovement]: { Icon: VerifiedUser, tone: 'info' },
    [MessageType.EmergencyStop]: { Icon: BackHand, tone: 'error' },
    [MessageType.FinalizedWithError]: { Icon: ErrorOutline, tone: 'error' },
    [MessageType.LowerLimit]: { Icon: ArrowDownward, tone: 'warning' },
    [MessageType.UpperLimit]: { Icon: ArrowUpward, tone: 'warning' },
    [MessageType.ManualMode]: { Icon: Engineering, tone: 'warning' },
  };

  return iconMap[type] ?? DEFAULT_ATTRIBUTES;
};

export const getTextFormatters = (
  type: MessageType,
  i18n: I18n,
  formatters: Formatters,
): TextFormatters => {
  const DEFAULT_TEXT_FORMATTERS: TextFormatters = {
    titleFormatter: () => t(i18n)`Mensaje de la compuerta`,
  };

  /**
   * Causa real del cierre, cuando el mensaje la trae. Los tres tipos terminales colapsan códigos
   * que el operario necesita distinguir —un atasco, un timeout y una falla del actuador se
   * arreglan de maneras distintas—, así que el texto del código gana sobre el genérico del tipo.
   * Sin código se conserva el texto de siempre: es todo lo que se sabe del mensaje.
   */
  const closeOf = (payload: NotificationData['payload']) =>
    describeGateCloseCode(payload?.closeCode, i18n);

  /**
   * Posición reportada por el mensaje, ya formateada, o el marcador de desconocido.
   *
   * Todos los formatters que muestran una posición pasan por acá: la lectura del payload es una
   * sola (`readPayloadPosition`, espejo del backend) y el criterio de «no hay dato» también. Antes
   * cada formatter repetía el chequeo inline y miraba solo la clave legacy `position`, así que el
   * panel mostraba «?» en todos los mensajes del firmware nuevo, que publica `pos` (AMARU-887).
   * `0` es la compuerta cerrada, no ausencia de dato (AMARU-869).
   */
  const positionOf = (payload: NotificationData['payload']) => {
    const position = readPayloadPosition(payload);
    if (position == null) return '?';
    return formatPosition(position, formatters.formatMetricValue);
  };

  const formattersMap: Record<MessageType, TextFormatters> = {
    [MessageType.Initializing]: {
      titleFormatter: () => t(i18n)`Iniciando comunicación con la compuerta`,
      contentFormatter: () => t(i18n)`Se está enviando la solicitud de operación a la compuerta`,
    },
    [MessageType.StopOperation]: {
      titleFormatter: (payload) =>
        closeOf(payload)?.title ?? t(i18n)`Operación incompleta - Se ha detenido el movimiento`,
      contentFormatter: (payload) =>
        closeOf(payload)?.detail ??
        t(i18n)`Se ha detenido el movimiento de la compuerta remotamente`,
    },
    [MessageType.Complete]: {
      // El título distingue el completado (GO01) del fin de carrera (GO02), que llegan con el
      // mismo tipo; el contenido sigue siendo la posición alcanzada, que es el dato operativo.
      titleFormatter: (payload) => closeOf(payload)?.title ?? t(i18n)`Operación completada`,
      contentFormatter: (payload) => {
        // El binding tiene nombre a propósito: el macro de Lingui arma el placeholder del msgid
        // con la expresión, y una llamada inline lo convertiría en `{0}`, invalidando el catálogo.
        const position = positionOf(payload);
        return t(i18n)`Compuerta ha llegado a la posición ${position}`;
      },
    },
    [MessageType.Battery]: {
      titleFormatter: () => t(i18n)`Nivel de batería`,
      contentFormatter: (payload) => {
        let battery = '?';
        if (payload && 'batteryLevel' in payload && typeof payload.batteryLevel === 'number') {
          battery = formatters.formatMetricValue(payload.batteryLevel, {
            unit: MetricUnit.Percent,
            precision: 1,
          });
        }
        return t(i18n)`El nivel de la batería es ${battery}`;
      },
    },
    [MessageType.Movement]: {
      titleFormatter: (payload, Accent = DefaultAccent) => {
        const position = positionOf(payload);
        return (
          <Trans>
            Compuerta en movimiento: - <Accent>posición actual: {position}</Accent>
          </Trans>
        );
      },
    },
    [MessageType.ValidateMovement]: {
      titleFormatter: () => t(i18n)`Compuerta ha validado el movimiento`,
      contentFormatter: () => t(i18n)`Iniciando el movimiento solicitado`,
    },
    [MessageType.EmergencyStop]: {
      titleFormatter: (payload) =>
        closeOf(payload)?.title ?? t(i18n)`Operación incompleta - Parada de emergencia ACTIVA`,
      contentFormatter: (payload) =>
        closeOf(payload)?.detail ??
        t(i18n)`No se podrá operar hasta su deshabilitación en el tablero compuerta`,
    },
    [MessageType.FinalizedWithError]: {
      titleFormatter: (payload) =>
        closeOf(payload)?.title ?? t(i18n)`Operación finalizada con error`,
      contentFormatter: (payload) =>
        closeOf(payload)?.detail ?? t(i18n)`La operación no se completó correctamente`,
    },
    [MessageType.LowerLimit]: {
      titleFormatter: () => t(i18n)`Compuerta alcanzó el límite inferior`,
    },
    [MessageType.UpperLimit]: {
      titleFormatter: () => t(i18n)`Compuerta alcanzó el límite superior`,
    },
    [MessageType.ManualMode]: {
      titleFormatter: () => t(i18n)`Compuerta en modo manual`,
      contentFormatter: () => t(i18n)`La compuerta está operando en modo manual`,
    },
  };
  return formattersMap[type] ?? DEFAULT_TEXT_FORMATTERS;
};
